import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNotificationContext } from '../context'
import { BUILD_ENV } from '../helpers'
import { useCopyToClipboard } from '../hooks/use-copy-to-clipboard'
import { AnimatedCopyIcon } from './motion'

const InviteLink = ({ careerID, partnerID }) => {
  const { t } = useTranslation()
  const { copied, copy } = useCopyToClipboard({ resetAfter: 1000 * 3 })
  const { success, error } = useNotificationContext()

  const inviteLink = useMemo(() => {
    const DEVELOP_URL = new URL('https://develop.pbc.aivy.app/invitation/')
    const STAGING_URL = new URL('https://staging.pbc.aivy.app/invitation')
    const PRODUCTION_URL = new URL('https://pbc.aivy.app/invitation/')

    const param = {
      name: careerID ? 'career' : 'partner',
      value: careerID || partnerID
    }

    DEVELOP_URL.searchParams.set(param.name, param.value)
    STAGING_URL.searchParams.set(param.name, param.value)
    PRODUCTION_URL.searchParams.set(param.name, param.value)

    switch (BUILD_ENV) {
      case 'develop':
        return DEVELOP_URL.toString()
      case 'staging':
        return STAGING_URL.toString()
      default:
        return PRODUCTION_URL.toString()
    }
  }, [careerID, partnerID])

  const copyInviteLinkToClipboard = async () => {
    if (!(await copy(inviteLink))) {
      error(t('global.copy_error'))
      return
    }

    success(t('career.public_invitation_link.copy_success'))
  }

  return (
    <div className='rounded-md bg-gray-50 px-6 py-5'>
      <div className='flex items-center'>
        <div>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            fill='none'
            viewBox='0 0 24 24'
            strokeWidth='1.5'
            stroke='currentColor'
            className='h-6 w-6'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244'
            />
          </svg>
        </div>
        <code className='ml-2 mr-4 truncate text-xs text-gray-900'>
          <a
            className='text-blue-700 underline visited:text-blue-800 hover:text-blue-500'
            href={inviteLink}
            target='_blank'
            rel='noreferrer'
          >
            {inviteLink}
          </a>
        </code>
        <button
          type='button'
          title={t('career.public_invitation_link.copy')}
          aria-label={t('career.public_invitation_link.copy')}
          className='ml-auto flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-primaryBlue hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
          onClick={copyInviteLinkToClipboard}
        >
          <AnimatedCopyIcon
            copied={copied}
            icon={ClipboardDocumentListIcon}
            className='size-5 text-white'
            checkClassName='text-white'
          />
        </button>
      </div>
    </div>
  )
}

InviteLink.propTypes = {
  careerID: PropTypes.string,
  partnerID: PropTypes.string
}

export default InviteLink
